/**
 * The notification sound.
 *
 * ── WHY IT IS SYNTHESISED AND NOT AN MP3 ────────────────────────────────────
 *
 * An audio file is one more asset to ship, to cache-bust, to get wrong in a
 * subdirectory deploy, and to 404 silently — and a notification sound that
 * 404s is indistinguishable from one nobody turned on. Two sine tones through
 * the Web Audio API is about forty lines, weighs nothing, and cannot go
 * missing.
 *
 * ── WHY IT CANNOT JUST PLAY ─────────────────────────────────────────────────
 *
 * Browsers refuse to make noise until the person has interacted with the page.
 * That is not a bug to work around: a site that makes a sound before anybody
 * has touched it is a site people close. So the context is created lazily on
 * the first real interaction and stays suspended until then, and every call
 * here is safe to make at any time — it simply does nothing when it cannot.
 *
 * ── AND WHY MUTE IS REMEMBERED ──────────────────────────────────────────────
 *
 * An agent works the support desk all day. A sound they cannot turn off is a
 * sound they solve by turning off the speakers, which also loses them the one
 * alert that mattered. The preference is per browser, in localStorage, and
 * defaults to ON because a notification nobody hears is not a notification.
 */

const MUTE_KEY = 'chicare.sound.muted';

let context: AudioContext | null = null;
let armed = false;

/** Whether sound is wanted here. Defaults to on. */
export function soundMuted(): boolean {
  try {
    return window.localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    // Private window or blocked storage. Audible is the safer default: a
    // missed delivery notification costs more than an unwanted beep.
    return false;
  }
}

export function setSoundMuted(muted: boolean): void {
  try {
    window.localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* Nothing to do, and nothing worth telling anybody. */
  }
}

/**
 * Start listening for the first interaction.
 *
 * Called once from the app shell. Until somebody clicks, taps or presses a
 * key, no AudioContext exists at all — so nothing is holding audio hardware
 * open on a page that may never make a sound.
 */
export function armSound(): void {
  if (armed || typeof window === 'undefined') return;

  armed = true;

  const wake = () => {
    try {
      context ??= new (window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      void context.resume();
    } catch {
      // No Web Audio. The badges and the tab title still work, which is the
      // half that matters.
    }

    window.removeEventListener('pointerdown', wake);
    window.removeEventListener('keydown', wake);
  };

  window.addEventListener('pointerdown', wake, { once: true });
  window.addEventListener('keydown', wake, { once: true });
}

/**
 * Two short tones, rising.
 *
 * Deliberately quiet and deliberately short. This fires on a phone in a
 * pharmacy and on a laptop in an open office; anything longer or louder is
 * something people disable on the second day.
 */
export function chime(): void {
  if (soundMuted() || !context || context.state !== 'running') return;

  try {
    const now = context.currentTime;

    // A fifth apart — recognisably an alert rather than a click, without
    // being a ringtone.
    // Typed as a tuple list, not number[][] — destructuring the latter gives
    // `number | undefined` for every element and TypeScript is right to say so.
    const tones: Array<[frequency: number, offset: number]> = [
      [880, 0],
      [1320, 0.12],
    ];

    tones.forEach(([frequency, offset]) => {
      const oscillator = context!.createOscillator();
      const gain = context!.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      // Ramped, not switched. A square-edged gain change is an audible click
      // on most hardware, which reads as a fault rather than a notification.
      gain.gain.setValueAtTime(0, now + offset);
      gain.gain.linearRampToValueAtTime(0.09, now + offset + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.18);

      oscillator.connect(gain).connect(context!.destination);
      oscillator.start(now + offset);
      oscillator.stop(now + offset + 0.2);
    });
  } catch {
    /* Audio is a courtesy. It must never take a page down. */
  }
}
